import { Calendar, Call, Clock, Location as MapPin, Sms } from '@/components/icons';
import { PatientPhotoRegenerateButton } from '@/components/patients/PatientPhotoRegenerateButton';
import { PageSection } from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDateOnly } from '@/lib/date';
import { getAge, translateGender } from '@/utils';
import type { Patient } from '@/types/patient';
import type { PatientAnamnesis } from '@/types/patient-anamnesis';

type Props = {
    patient: Patient;
    isRegeneratingPhoto: boolean;
    onRegeneratePhoto: () => void;
    anamnesis?: PatientAnamnesis | null;
};

export default function PatientHero({ patient, isRegeneratingPhoto, onRegeneratePhoto }: Props) {
    return (
        <PageSection contentClassName="p-6">
            <div className="flex items-start gap-4">
                <PatientPhotoRegenerateButton
                    name={patient.full_name}
                    avatarUrl={patient.avatar_url}
                    className="size-16 rounded-2xl"
                    fallbackClassName="text-xl text-brand"
                    isLoading={isRegeneratingPhoto}
                    onRegenerate={onRegeneratePhoto}
                />
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <h2 className="text-2xl font-semibold text-foreground">
                                {patient.full_name}
                            </h2>
                            {patient.intake_pending && (
                                <TooltipProvider delayDuration={0}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="cursor-help text-warning">
                                                <Clock className="size-4" />
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            Aguardando resposta do formulário de anamnese
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        {patient.gender && (
                            <Badge variant="outline" className="text-xs capitalize">
                                {translateGender(patient.gender)}
                            </Badge>
                        )}
                        {patient.date_of_birth && (
                            <Badge variant="neutral" className="text-xs">
                                {getAge(patient.date_of_birth)} anos
                            </Badge>
                        )}
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                        {patient.email && (
                            <div className="flex items-center gap-2">
                                <Sms className="size-4 text-muted-foreground" />
                                <span className="truncate">{patient.email}</span>
                            </div>
                        )}
                        {patient.phone && (
                            <div className="flex items-center gap-2">
                                <Call className="size-4 text-muted-foreground" />
                                <span>{patient.phone}</span>
                            </div>
                        )}
                        {patient.city && (
                            <div className="flex items-center gap-2">
                                <MapPin className="size-4 text-muted-foreground" />
                                <span>{patient.city}</span>
                            </div>
                        )}
                        {patient.date_of_birth && (
                            <div className="flex items-center gap-2">
                                <Calendar className="size-4 text-muted-foreground" />
                                <span>{formatDateOnly(patient.date_of_birth)}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageSection>
    );
}
